import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, of, switchMap, throwError } from 'rxjs';
import { Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { safeJsonParse } from 'src/app/shared/utils/safe-json';
import { LocalStorageService } from './local-storage.service';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import Web3 from 'web3';
import { ToastrService } from 'ngx-toastr';
import { WagmiConfigService, BSC_SWITCH_REQUIRED_MESSAGE } from './wagmi-config.service';
import { signMessage } from '@wagmi/core';
import { firstValueFrom } from 'rxjs';
import { getWalletAddress } from 'src/app/shared/utils/wallet-state';

@Injectable({
  providedIn: 'root'
})
export class Web3AuthService {

  public walletAddress: any
  public walletAddressObserve: any;
  public storage!: LocalStorageService;
  // private web3Service: Web3Service;
  public account: any;
  public accountObservable!: Observable<any>;
  public chainId: any
  public web3: any;
  public isMetaMaskConnected: boolean = false
  public connectedChainId: any
  public isCreateAccountOpened: any
  public networkDetails: any = {
    chainId: environment.ALLOWED_NETWORKS
  }
  public currentNativeCurrency: any
  public currentNetworkName: any
  public currentNetworkSymbol: any
  public isUserAuth: any
  public isUserAuthObservable: any
  public isSignup: any = false
  public isSignupObserve: any
  isconnect: boolean = false;
  // De-dupes only a request that is *still running* (the entry is dropped the moment it settles),
  // so two callers firing in the same tick share one round-trip while any later call always
  // fetches again. Deliberately not a cache: a nonce is single-use, so a completed result must
  // never be replayed for a second signup/login attempt.
  private nonceInFlight = new Map<string, Promise<{ nonce_token: string; message: string }>>();
  private signatureInFlight = new Map<string, Promise<string>>();


  constructor(
    private _storage: LocalStorageService,
    private authService: AuthService,
    private route: Router,
    private toastService: ToastrService,
    private http: HttpClient,
    private router: ActivatedRoute,
    private toastr: ToastrService,
    private wagmiConfigService: WagmiConfigService
  ) {
    this.isSignup = new BehaviorSubject(false);
    this.isSignupObserve = this.isSignup.asObservable();
   }

  /**
   * Fetches a login nonce for this wallet. Every call hits the API: nonces are single-use, so a
   * result is never cached or reused across attempts — signup and login both always get a fresh
   * one.
   */
  public async requestNonce(walletAddress: string): Promise<{ nonce_token: string; message: string }> {
    const address = (walletAddress ?? '').toLowerCase();

    const existing = this.nonceInFlight.get(address);
    if (existing) return existing;

    const task = (async () => {
      const response: any = await firstValueFrom(
        this.http.get(`${environment.API_BASE_URL}api/v1/user/nonce?wallet_address=${address}`)
      );
      const value = {
        nonce_token: response?.data?.nonce_token,
        message: response?.data?.message
      };
      if (!value.nonce_token || !value.message) {
        // Proxies/intermediate errors can return unexpected shapes; fail loudly rather than
        // letting a partial payload reach the signature step.
        throw new Error('Invalid nonce response from server.');
      }
      return value;
    })();

    this.nonceInFlight.set(address, task);
    try {
      return await task;
    } finally {
      this.nonceInFlight.delete(address);
    }
  }

  /**
   * Signs the login message with the connected wallet. Every call prompts the wallet: a signature
   * is only valid for the nonce baked into its message, and each nonce is minted fresh, so there
   * is nothing to reuse.
   *
   * Refuses to prompt while the wallet is off the configured BSC chain (or while Wagmi's
   * connector / connection chain ids still disagree). Callers that already ran
   * `ensureCorrectChain()` still benefit: this is the last gate before `signMessage`.
   */
  public async signLoginMessage(walletAddress: string, message: string): Promise<string> {
    const wagmiConfig = this.wagmiConfigService.getWagmiConfig();
    if (!wagmiConfig) {
      throw new Error('Wagmi config not initialized. Call wagmiConfiguration() before signing messages.');
    }

    const onExpectedChain = await this.wagmiConfigService.ensureCorrectChain({ showToasts: false });
    if (!onExpectedChain) {
      throw Object.assign(new Error(BSC_SWITCH_REQUIRED_MESSAGE), { name: 'ConnectorChainMismatchError' });
    }

    const address = (walletAddress ?? '').toLowerCase();
    const key = `${address}|${message ?? ''}`;

    const existing = this.signatureInFlight.get(key);
    if (existing) return existing;

    const task = (async () => {
      // @wagmi/core v2 takes the config as the first argument; the active connector/account is
      // still resolved from it under the hood.
      try {
        return await signMessage(wagmiConfig, { message });
      } catch (error) {
        if (WagmiConfigService.isChainMismatchError(error)) {
          throw Object.assign(new Error(BSC_SWITCH_REQUIRED_MESSAGE), { name: 'ConnectorChainMismatchError', cause: error });
        }
        throw error;
      }
    })();

    this.signatureInFlight.set(key, task);
    try {
      return await task;
    } finally {
      this.signatureInFlight.delete(key);
    }
  }

  public async loginWithSignature(
    walletAddress: string,
    nonce_token: string,
    signature: string,
    message: string
  ): Promise<any> {
    const payload = {
      wallet_address: walletAddress,
      nonce_token,
      signature
    };

    const result: any = await this.login(payload);
    const sessionToken = result?.data?.session?.session_token;
    if (sessionToken) {
      this._storage.setToken(sessionToken);
    }
    return result;
  }

  private async signup(params: any) {    
    return new Promise((resolve, reject) => {
      this.authService.signUp(params).subscribe({
        next: (response: any) => {
          resolve({ status: true, data: response.data });
        },
        error: (error) => {
          reject({ status: false, data: error });
        },
      });
    });
  }


  /**
   * Sign up a user with the given userRegisterData and isCollaborativeUser flag.
   * If isCollaborativeUser is true, it will call the collaborativeUserSignUp method.
   * If isCollaborativeUser is false, it will call the signup method.
   * After the signup, it will get the user details using the user's wallet address.
   * If the user does not have a name, it will open the create account popup.
   * It will navigate to the contributenow route.
   * @param userRegisterData The user registration data.
   * @param isCollaborativeUser The flag to indicate if the user is a collaborative user.
   * @returns A promise that resolves with an object containing the user details and a status flag.
   */
   private async collaborativeUserSignUp(params: any) {    
    return new Promise((resolve, reject) => {
      this.authService.collaborativeSignUp(params).subscribe({
        next: (response: any) => {
          resolve({ status: true, data: response.data });
        },
        error: (error) => {
          reject({ status: false, data: error });

        },
      });
    });
  }

  checkTwitterProfile(username: string): Observable<any> {
    const url = `https://api.twitter.com/1.1/users/show.json?screen_name=${username}&callback=JSONP_CALLBACK`;
    return this.http.jsonp(url, 'JSONP_CALLBACK');
  }

  checkFacebookProfile(username: string) {
    // Construct the URL for the Facebook Graph API
    const url = `https://graph.facebook.com/${username}`;

    // Send a GET request to Facebook's Graph API
    return this.http.get(url);
  }

    /**
   * Logins navbar component
   * @param params
   * @returns
   */
    public async login(params: any) {
      return new Promise((resolve, reject) => {
        this.authService.signin(params).subscribe({
          next: (response: any) => {
            resolve({ status: true, data: response.data });
            const url = localStorage.getItem('currentUrl');
            if(url == '/signup'){
              localStorage.removeItem('currentUrl')
              this.route.navigate(['/user-dashboard']);
            }
          },
          error: (error) => {
            reject({ status: false, data: error });
          },
        });
      });
    }

    private async getUser(params: any) {
      return new Promise((resolve, reject) => {
        this.authService.getUser(params).subscribe({
          next: (response: any) => {
            resolve({ status: true, data: response.data });
          },
          error: (error) => {
            reject({ status: false, data: error });
          },
        });
      });
    }

      /**
  * Manages account
  */
  public async manageAccount() {
    // login or register api call
    if (!this._storage.getToken()) {
      this.getUser(this.walletAddress)
        .then(async (result: any) => {
          await this.login(this.walletAddress)
            .then(async (response: any) => {
              this._storage.setToken(
                response.data.session.session_token
              );
              this.isUserAuth = response.data.session.session_token
              setTimeout(() => {
                this.route.navigate([''])
              }, 100)
            })
            .catch((err) => {
              this.toastService.error(err.data.error.message);
              setTimeout(() => {
                localStorage.clear()
                window.location.reload()
              }, 4000)
            });
          const user = result.data;
          if (!user.hasOwnProperty('name')) {
            this.isCreateAccountOpened = true;
          }
        }).catch((err) => {
          if (err.data.status == 400) {
            setTimeout(() => {
              this.isSignup.next(true)
            }, 100)
          }
        });

    } else {
      this.getUser(this.walletAddress)
        .then(async (result: any) => {
          const user = result.data;
          await this.login(this.walletAddress)
            .then(async (response: any) => {
              this._storage.setToken(
                response.data.session.session_token
              );
              this.isUserAuth = response.data.session.session_token
            })
            .catch((err) => {
              console.error(err)
            });
          // }
          if (!user.hasOwnProperty('name')) {
            this.isCreateAccountOpened = true;
          }
        })
        .catch((err) => {
          console.error(err)
          // need to open invalid session popup

          // this.toastr.error(err.data?.message);
        });
    }
  }

  /**
   * Signup a user with the given userRegisterData and isCollaborativeUser flag.
   * If isCollaborativeUser is true, it will call the collaborativeUserSignUp method.
   * If isCollaborativeUser is false, it will call the signup method.
   * After the signup, it will get the user details using the user's wallet address.
   * If the user does not have a name, it will open the create account popup.
   * It will navigate to the contributenow route.
   * @param userRegisterData The user registration data.
   * @param isCollaborativeUser The flag to indicate if the user is a collaborative user.
   * @returns A promise that resolves with an object containing the user details and a status flag.
   */
referalSignUp(userRegisterData,isCollaborativeUser): Promise<any> {
  return new Promise((resolve, reject) => {
    if(isCollaborativeUser){
      this.collaborativeUserSignUp(userRegisterData)
      .then((response: any) => {
         this._storage.setToken(response.data.session.session_token);
        this.isUserAuth = response.data.session.session_token;
        this.walletAddress = getWalletAddress();
        this.authService.getUser(this.walletAddress).subscribe({
          next: (result: any) => {
            const user = result.data;
            if (!user.hasOwnProperty('name')) {
              this.isCreateAccountOpened = true;
            }
            this.route.navigate(['contributenow']);
            resolve({ status: true, data: user });
          },
          error: ((err) => {            
            reject({ status: false, data: err });
          })
        })
      }).catch((err) => {
        reject({ status: false, data: err });
      })
    }else{
    this.signup(userRegisterData)
      .then((response: any) => {
        this._storage.setToken(response.data.session.session_token);
        this.isUserAuth = response.data.session.session_token;
        this.walletAddress = getWalletAddress();
        this.authService.getUser(this.walletAddress).subscribe({
          next: (result: any) => {
            const user = result.data;
            if (!user.hasOwnProperty('name')) {
              this.isCreateAccountOpened = true;
            }
            this.route.navigate(['contributenow']);
            resolve({ status: true, data: user });
          },
          error: ((err) => {            
            reject({ status: false, data: err });
          })
        })
      })
      .catch((err) => {
        reject({ status: false, data: err });
      });
    }
  });  
}

  /**
   * Gets contract params
   * @param contractType
   * @returns
   */
  public async getContractParams(contractType: string, contractAddress: string) {
    return {
      contractAddress: contractAddress,
      abi: require('../../../contracts' +
        '/' +
        contractType +
        '.json'),
    };
  }

  /**
   * Gets contract by address
   * @param contractType
   * @param contractAddress
   * @returns
   */
  public async getContract(
    contractType: string,
    contractAddress: string
  ) {
    const params = await this.getContractParams(contractType, contractAddress);
    const web3 = new Web3(environment.RPC_URL);
    return await new web3.eth.Contract(params.abi, contractAddress);
  }

  /**
   * Gets country code
   * @returns  
   */
  public getCountryCode() {
    return this.http.get(`${environment.COUNTRY_CODE_BASE_URL}`);
  }

  public decimalDivider(divider: number = 1, value: number = 0) {
    // @ts-ignore    
    const web3 = new Web3(window['ethereum']);
    let originalValue;
    try {
      switch (Number(divider)) {
        case 18: {
          let weiValue = web3.utils.fromWei(value.toString(), 'ether');
          originalValue = weiValue;
          break;
        }

        case 9: {
          let weiValue = web3.utils.fromWei(value.toString(), 'gwei');
          originalValue = weiValue;
          break;
        }

        case 6: {
          let weiValue = web3.utils.fromWei(value.toString(), 'mwei');
          originalValue = weiValue;
          break;
        }

        default: {
          originalValue = Number(value) / 10 ** (18 - divider);
          break;
        }
      }
      return originalValue;
    } catch (e) {
    } finally {
      return originalValue;
    }
  }

  /**
   * Gets wallet address from local storage
   * @returns wallet address
   */
  // Return type stays `any` so the callers that assign this straight into `string` fields keep
  // compiling exactly as before; the runtime value is unchanged.
  async getWalletAddress(): Promise<any> {
    return getWalletAddress();
  }
}
